/** Plan comercial de un grupo (`AnalisisGrupoResponse.nitrofertPlan`).
 *
 *  ⚠️ **Sin contrato estable**: el backend lo guarda como JSON opaco tal cual lo
 *  devuelve el resolvedor L3, y el schema `PlanResponse` del OpenAPI está
 *  incompleto. Por eso se parsea a mano acá, con todo opcional y descartando lo
 *  que no cumple: si L3 cambia el shape, rompe **este archivo** y no la
 *  pantalla. No consumir el JSON crudo desde ningún otro lado. */

/** Un producto del plan de un grupo. */
export type NitrofertPlanProducto = {
  /** Nombre comercial (ej. "KCL Gr"). */
  product: string;
  /** Línea del producto (ej. "simple"). */
  line: string | null;
  /** Rol dentro del plan (`base_npk` | `k_source` | `supplement`…). */
  role: string | null;
  /** Dosis por hectárea. */
  kgHa: number | null;
  /** Total para el área del grupo (`kgHa` × área del grupo). */
  kgTotal: number | null;
  /** Óxidos que aporta el producto → kg. **Las claves vienen en minúscula y no
   *  coinciden con `AnalisisNutrienteResponse.nutrient`** (ej. `s` ↔ `SO3`):
   *  traducir con `nutrienteDesdeSupplied`. */
  nutrientsSupplied: Record<string, number>;
  /** `true` si el producto es libre de cloro. */
  clFree: boolean;
  /** Nota del producto. */
  note: string | null;
};

/** Plan comercial de un grupo, ya normalizado. */
export type NitrofertPlan = {
  /** Área del grupo **completo** en hectáreas (= `AnalisisGrupoResponse.totalAreaHa`). */
  areaHa: number | null;
  /** Lote de **referencia** del grupo — no es la etiqueta del grupo. */
  loteReferencia: string | null;
  /** Productos resueltos para el grupo. */
  productos: NitrofertPlanProducto[];
  /** Óxido (en minúscula) → porcentaje cubierto por los productos. */
  coverage: Record<string, number>;
  /** Óxidos que **Nitrofert no puede cubrir con ningún producto**. Ojo: no es lo
   *  mismo que `coverage < 100` — un parcial por tope de dosis queda en
   *  `warnings` pero no acá. Un óxido en `coverage: 0` aparece en ambos. */
  uncovered: string[];
  /** Avisos en castellano, listos para mostrar al usuario. */
  warnings: string[];
};

/** `true` si el valor es un objeto plano navegable. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Número finito, o `null`. Acepta el número serializado como string. */
function toNumber(value: unknown): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/** String no vacío, o `null`. */
function toText(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value : null;
}

/** Array de strings no vacíos (descarta el resto). */
function toTextList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string");
}

/** Mapa clave → número (descarta las entradas no numéricas). */
function toNumberRecord(value: unknown): Record<string, number> {
  if (!isRecord(value)) return {};
  const result: Record<string, number> = {};
  for (const [key, raw] of Object.entries(value)) {
    const parsed = toNumber(raw);
    if (parsed !== null) result[key] = parsed;
  }
  return result;
}

/** Parsea un producto; `null` si no tiene ni nombre. */
function parseProducto(value: unknown): NitrofertPlanProducto | null {
  if (!isRecord(value)) return null;
  const product = toText(value.product);
  if (!product) return null;
  return {
    product,
    line: toText(value.line),
    role: toText(value.role),
    kgHa: toNumber(value.kg_ha),
    kgTotal: toNumber(value.kg_total),
    nutrientsSupplied: toNumberRecord(value.nutrients_supplied),
    clFree: value.cl_free === true,
    note: toText(value.note),
  };
}

/** Parsea el `nitrofertPlan` de un grupo. Devuelve `null` si no es un objeto
 *  (grupo sin plan, o L3 falló — ver `AnalisisGrupoResponse.l3Error`). */
export function parseNitrofertPlan(raw: unknown): NitrofertPlan | null {
  if (!isRecord(raw)) return null;
  const productos = Array.isArray(raw.products)
    ? raw.products
        .map(parseProducto)
        .filter((producto): producto is NitrofertPlanProducto => producto !== null)
    : [];
  return {
    areaHa: toNumber(raw.area_ha),
    loteReferencia: toText(raw.lot_name),
    productos,
    coverage: toNumberRecord(raw.coverage),
    uncovered: toTextList(raw.uncovered),
    warnings: toTextList(raw.warnings),
  };
}
